import { DOCUMENT } from '@angular/common';
import { Component, computed, ElementRef, Inject, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  from,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs';

import {
  ConfigurationService,
  IProviderShareableLinkAppConfig,
} from '@sondermind/configuration';
import {
  IAppointmentSlot,
  ProviderCalendarStore,
} from '@sondermind/data-access/public-facing/provider-calendar';
import {
  ProviderAvailability,
  ProviderAvailabilityMember,
  ProviderRelationship,
  SchedulingAvailabilityService,
  TimeSlot,
} from '@sondermind/data-access/scheduling';
import { IProviderCalendarInitSegmentData } from '@sondermind/features/public-facing/provider-calendar';
import {
  IRequestSessionParams,
  RequestASessionService,
} from '@sondermind/features/public-facing/session-request';
import { LandingPageContact } from '@sondermind/features/support-phone-number-card';
import {
  GtmCustomEvents,
  GtmDataLayerService,
} from '@sondermind/google-tag-manager';

import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import { NativeAppContextHelpers } from '@sondermind/native-app-interface';
import { RecaptchaService } from '@sondermind/recaptcha';
import { AiConnectComponent, AiConnectProjectType, SegmentService } from '@sondermind/segment';
import { IAPIFetchResponse } from '@sondermind/utilities/models-http';
import {
  ILocation,
  IProviderShareableProfile,
} from '@sondermind/utilities/models-public-facing';
import { ISessionRequestPreferences } from '@sondermind/utilities/models-scheduling';

import { USStateAbbrevMap } from '@sondermind/utilities/states-list';
import { WINDOW } from '@sondermind/utilities/tools';

import { SessionPreference } from '@sondermind/utilities/models-session';
import { AppService } from '@sondermind/data-access/app-service';
import {
  BookingFlow,
  BookingStates,
  DirectBookingComponentService,
  DirectBookingData
} from '@sondermind/features/direct-booking';
import { ClientReferralMembersStore, IProvider, ProviderStore } from '@sondermind/data-access/client-portal-referrals';

import { DirectoryLinkService } from '@sondermind/data-access/directory-link';
import { DirectoryCompanionVariant, IFindMyFitChip } from '@sondermind/features/ai-connect-provider-directory';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';
import { NotificationToastService, ToastType } from '@sondermind/features/notification-toast';
import { LocalizedSupportResourceService } from '@sondermind/utilities/support-resources';
import { BannerVariant } from '../landing-page-banner/landing-page-banner.component';
import { IPslPageLoadEventData } from '../../models/segment/segment-event-data.interface';
import { ITopSpecialty } from '../../models/top-specialty.interface';
import { ShareableLinksHttpService } from '../../services/shareable-links.httpservice';

const MOBILE_BOTTOM_DRAWER_HEIGHT = 120;
@Component({
  selector: 'landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  standalone: false,
})
export class LandingPageComponent {
  providerShareableProfile: IProviderShareableProfile =
    {} as IProviderShareableProfile;
  isValid: boolean = false;
  isLoading: boolean = true;

  readonly clientPortalProvider = signal<IProvider>({} as IProvider);
  readonly isClientPortalProviderLoading = signal<boolean>(true);
  readonly hasMatchedWithProvider = signal<boolean>(false);
  readonly providerSchedulingEnabled = computed<boolean>(() => {
    if (!this.hasMatchedWithProvider()) return true;
    const provider = this.clientPortalProvider();
    return provider?.hasDirectScheduling || provider?.hasProposeATime;
  });
  clientReferralMemberId: number | undefined = undefined;
  therapyMatchFlowUrl: string = 'https://start.sondermind.com';

  @ViewChild('actions', { static: false })
  private actionsCard!: ElementRef<HTMLElement>;
  isCalendarScrolledIntoView!: boolean;

  calendarSegmentDataSubj: BehaviorSubject<IProviderCalendarInitSegmentData | null> =
    new BehaviorSubject<IProviderCalendarInitSegmentData | null>(null);
  calendarSegmentData$: Observable<IProviderCalendarInitSegmentData | null> =
    this.calendarSegmentDataSubj.asObservable();

  currentStateAbbrev$ = new BehaviorSubject<string>('');
  directoryUrl$ = this.currentStateAbbrev$.pipe(
    map((state) => {
      const baseUrl = this.configurationService.env.providerDirectoryBase
        || 'https://www.sondermind.com/provider-directory';
      const stateSegment = state?.toLowerCase() || 'all';
      return `${baseUrl}/sta-${stateSegment}/cty-all`;
    })
  );
  soonestAvailableDate$!: Observable<Date | null>;
  paramsShareCode: string = '';
  paramsName: string = '';
  requestSessionQueryParams: IRequestSessionParams | null = null; // For tracking the user coming from .com site
  topSpecialties: ITopSpecialty[] = [];
  providerPrimaryAddress: ILocation | null = null;

  queryParamMap!: ParamMap;
  queryParams!: Params;

  appointmentSlots$!: Observable<IAppointmentSlot[]>;
  isCalendarLoading: boolean = true;
  showProviderCalendar$: Observable<boolean> = of(false);

  destroyed$ = new Subject<void>();

  supportPhoneNumberLink: string = LandingPageContact.MATCH_BY_PHONE_LINK;
  supportPhoneNumberDisplay: string = LandingPageContact.MATCH_BY_PHONE_DISPLAY;
  /**
   * Only true when a user schedules via the Native App AI Intake flow.
   */
  isScheduled: boolean = false;
  fromClientPortal: boolean = false;

  isUserLocaleOutsideAmerica = this.supportResourceService.isUserLocaleOutsideAmerica();

  private readonly directoryCompanionVariant = toSignal(
    this.featureFlagService.evaluate$(FeatureFlagKey.PROVIDER_DIRECTORY_AI_CONNECT, null).pipe(
      map((variant) => (variant?.key as DirectoryCompanionVariant) ?? DirectoryCompanionVariant.CONTROL)
    ),
    { initialValue: DirectoryCompanionVariant.CONTROL }
  );

  readonly directoryCompanionEnabled = computed(
    () => this.directoryCompanionVariant() !== DirectoryCompanionVariant.CONTROL
  );

  private isInternal: boolean = false;

  private readonly featureFlagVariant = toSignal(
    this.featureFlagService.evaluate$<boolean>(FeatureFlagKey.PSL_SOCIAL_PROOF_BANNER, false),
    { initialValue: null }
  );

  readonly bannerVariant = computed<BannerVariant | 'control'>(() => {
    const key = this.featureFlagVariant()?.key;
    return !key || key === 'off' || key === 'control' ? 'control' : (key as BannerVariant);
  });

  constructor(
    private route: ActivatedRoute,
    private shareableLinksHttpService: ShareableLinksHttpService,
    private configurationService: ConfigurationService<IProviderShareableLinkAppConfig>,
    private titleService: Title,
    private metaService: Meta,
    private gtmDataLayerService: GtmDataLayerService,
    private segmentService: SegmentService<SondermindEvents>,
    private requestASessionService: RequestASessionService,
    private recaptchaService: RecaptchaService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private window: Window,
    private router: Router,
    private providerCalendarStore: ProviderCalendarStore,
    private schedulingAvailabilityService: SchedulingAvailabilityService,
    private app: AppService,
    private directBookingService: DirectBookingComponentService,
    private providerStore: ProviderStore,
    private notificationToastService: NotificationToastService,
    private clientReferralMembersStore: ClientReferralMembersStore,
    private supportResourceService: LocalizedSupportResourceService,
    @Inject(FEATURE_FLAG_SERVICE) private featureFlagService: IFeatureFlagService,
    private directoryLinkService: DirectoryLinkService
  ) { }

  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap, this.route.queryParams])
      .pipe(take(1))
      .subscribe(([routeParams, queryParamMap, queryParams]) => {
        this.queryParams = queryParams;
        this.queryParamMap = queryParamMap;
        this.therapyMatchFlowUrl = this.configurationService.env.app.flowHost;

        this.paramsShareCode =
          this.requestASessionService.getShareCodeFromParams(routeParams);
        this.paramsName =
          this.requestASessionService.getNameFromParams(routeParams);
        this.requestSessionQueryParams =
          this.requestASessionService.parseQueryParams(queryParamMap);

        const routeData = this.route.snapshot.data;

        if (routeData?.['shareCode']?.['shareCode']) {
          this.paramsShareCode = routeData['shareCode']['shareCode'];
        }

        if (routeData?.['fromClientPortal']) {
          this.fromClientPortal = routeData['fromClientPortal'];
        }

        this.isScheduled = queryParamMap.get('scheduled') === 'true';
        this.loadProfile(this.paramsShareCode);

        // HACK: This causes the recaptcha icon to appear on the page, before we open the checkout.
        if (!this.fromClientPortal) {
          from(this.recaptchaService.executeEvent$('load'));
        }

        this.isInternal = queryParamMap.get('internal') === 'true';
      });
  }

  ngAfterViewInit(): void {
    this.app.ready();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onBookSessionButtonClicked(sessionPreferences?: ISessionRequestPreferences): void {
    let data: DirectBookingData;
    const hasSlot = !!sessionPreferences?.startTime;

    if (this.hasMatchedWithProvider()) {
      data = {
        providerPreview: this.clientPortalProvider(),
        bookingFlow: BookingFlow.EXISTING_CLIENT,
        clientReferralMemberId: this.clientReferralMemberId ?? 0,
        ...hasSlot
          ? { bookingState: BookingStates.INITIAL, slot: sessionPreferences }
          : { bookingState: BookingStates.INITIAL_WITHOUT_TIMESLOTS },
      } as DirectBookingData;
    } else {
      data = {
        providerPreview: this.clientPortalProvider(),
        bookingFlow: BookingFlow.NEW_CLIENT,
        ...hasSlot
          ? { bookingState: BookingStates.INITIAL, slot: sessionPreferences }
          : { bookingState: BookingStates.INITIAL_WITHOUT_TIMESLOTS },
      } as DirectBookingData;
    }

    this.directBookingService.openDialog(data).pipe(
      take(1)
    ).subscribe((res) => {
      if (res?.success) {
        this.notificationToastService.showNotificationToast({
          message: res.message,
          type: ToastType.SUCCESS
        });
      }
    });
  }

  private redirectoToUrlWithName() {
    const profileName = this.providerShareableProfile.display_name.toLocaleLowerCase().split(' ').join('-');
    this.router.navigate([`/${this.paramsShareCode}`, profileName],
      { queryParams: this.queryParams ? this.queryParams : {} }).catch(() => { });
  }

  private loadProfile(shareCode: string): void {
    this.shareableLinksHttpService
      .fetch$(shareCode)
      .pipe(
        takeUntil(this.destroyed$),
        catchError((error) => of(error)),
      )
      .subscribe((response: IAPIFetchResponse<IProviderShareableProfile>) => {
        if (Object.prototype.hasOwnProperty.call(response, 'error')) {
          this.isLoading = false;
          return;
        }

        this.providerShareableProfile = response.data;
        this.loadClientPortalProfile();

        if (!this.paramsName && !this.fromClientPortal) {
          this.redirectoToUrlWithName();
        }
        this.gtmDataLayerService.dataLayer = {
          event: GtmCustomEvents.PROVIDER_PAGE_VIEW,
          id: this.providerShareableProfile.id,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          full_name: this.providerShareableProfile.full_name,
        };
        this.updateMetaInfo(
          this.providerShareableProfile.display_name,
          this.providerShareableProfile.certifications,
          this.providerShareableProfile.bio
        );
        this.updateCanonicalUrl();

        // Avoids ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.isValid = true;
          this.isLoading = false;
        }, 0);

        this.topSpecialties = this.sortTopSpecialties(
          this.providerShareableProfile,
        );
        this.providerPrimaryAddress = this.findPrimaryInPersonAddress(
          this.providerShareableProfile,
        );

        this.initializeCalendar(this.providerShareableProfile);
        this.setOptimalStateForBrowseCard();

        const isRequestSession = this.queryParamMap.get('requestSession') === 'true';
        const startTime = this.queryParamMap.get('startTime');
        const endTime = this.queryParamMap.get('endTime');
        if (isRequestSession) {
          this.onRequestSessionButtonClicked({
            startTime: startTime ? new Date(startTime) : null,
            endTime: endTime ? new Date(endTime) : null,
            sessionType: startTime ? SessionPreference.VIDEO : null,
          });
        }

        this.sendPageViewSegmentEvent();
      });
  }

  private findPrimaryInPersonAddress(
    profile: IProviderShareableProfile,
  ): ILocation {
    return profile.locations.find(
      (location) => location.office_session_default === true,
    );
  }

  // top specialties are always sorted in alphabetical order
  private sortTopSpecialties(
    profile: IProviderShareableProfile,
  ): ITopSpecialty[] {
    const queryParamSpecialties =
      this.queryParamMap.get('specialties')?.split(',') ?? [];

    return profile.top_specialties
      .map((topSpecialty) => {
        const specialty = this.providerShareableProfile.specialties.find(
          (s) => topSpecialty.id === s.id,
        );
        return {
          ...specialty,
          isQueryParam: queryParamSpecialties.includes(specialty.name?.toString()),
          priorityOrder: topSpecialty.priority_order,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private initializeCalendar(profile: IProviderShareableProfile): void {
    if (this.fromClientPortal) return;

    this.appointmentSlots$ = this.providerCalendarStore.getAppointmentSlots$(
      this.providerShareableProfile.id,
      this.providerShareableProfile.time_zone,
    ).pipe(
      shareReplay(1),
    );

    this.assignCalendarObservables(profile);
  }

  private initializeAuthenticatedCalendar(profile: IProviderShareableProfile): void {
    const providerMembers: ProviderAvailabilityMember[] = [{
      providerId: profile.id,
      providerRelationship: this.hasMatchedWithProvider()
        ? ProviderRelationship.MATCHED
        : ProviderRelationship.OTHER,
    }];

    this.appointmentSlots$ = this.schedulingAvailabilityService
      .getAvailabilityForProviders(providerMembers)
      .pipe(
        map((availability: ProviderAvailability) => {
          const timeSlots: TimeSlot[] = availability.providerSlots.get(profile.id) ?? [];
          return timeSlots.map((ts) => ({
            displayStartTime: ts.start,
            utcStartTime: ts.start,
            utcEndTime: ts.end,
          }));
        }),
        shareReplay(1),
      );

    this.assignCalendarObservables(profile);
    this.showProviderCalendar$.pipe(take(1)).subscribe();
  }

  private assignCalendarObservables(profile: IProviderShareableProfile): void {
    this.showProviderCalendar$ = this.appointmentSlots$.pipe(
      map((appointmentSlots) => this.shouldShowCalendar(profile, appointmentSlots)),
      catchError(() => of(false)),
      tap(() => {
        this.isCalendarLoading = false;
      }),
      shareReplay(1),
    );

    this.soonestAvailableDate$ = this.appointmentSlots$.pipe(
      map((slots) => {
        const sortedSlots = [...slots].sort(
          (a, b) => a.utcStartTime.getTime() - b.utcStartTime.getTime()
        );
        return sortedSlots[0]?.utcStartTime;
      }),
      shareReplay(1),
    );
  }

  private shouldShowCalendar(
    profile: IProviderShareableProfile,
    appointmentSlots: IAppointmentSlot[],
  ): boolean {
    if (!profile.accepting_clients) {
      return false;
    }

    // if the provider has no default location (in-person or video), don't show the calendar
    const hasDefaultLocation = profile.locations.some(
      (location) => location.office_session_default || location.video_session_default,
    );

    if (!hasDefaultLocation) {
      return false;
    }

    // otherwise, show the calendar when it fits the abitrary (2 weeks) future time threshold
    const futureTimeThreshold = new Date(
      Date.now() +
      2 * // 2 weeks
      7 *
      24 *
      60 *
      60 *
      1000
    );
    const applicableSlots = appointmentSlots.filter((slot) => new Date(slot.utcStartTime) < futureTimeThreshold);
    // Are there any slots that fit the criteria?
    return applicableSlots.length > 0;
  }

  onCalendarInit(calendarData: IProviderCalendarInitSegmentData): void {
    this.calendarSegmentDataSubj.next(calendarData);
  }

  onNativeAppBookedASession(
    requestedSessionPreferences?: ISessionRequestPreferences,
  ): void {
    const startTime = requestedSessionPreferences?.startTime || null;
    const sessionType = requestedSessionPreferences?.sessionType || null;

    NativeAppContextHelpers.onPslBookedASession(
      this.providerShareableProfile.id,
      startTime,
      sessionType,
    );
  }

  loadClientPortalProfile(): void {
    if (!this.fromClientPortal) return;

    this.clientReferralMembersStore.getAll$().pipe(take(1)).subscribe();

    combineLatest({
      clientReferralMembers: this.clientReferralMembersStore.members$.pipe(
        map((members) => Array.from(members.values()))
      ),
      clientReferralMembersLoading: this.clientReferralMembersStore.isLoading$,
      provider: this.providerStore.loadProvider$(this.providerShareableProfile.provider_account_id)
    }).pipe(
      takeUntil(this.destroyed$),
      filter((results) => !results.clientReferralMembersLoading),
      tap((results) => {
        this.clientPortalProvider.set(results.provider);

        const crm = results.clientReferralMembers.find(
          (member) => member.providerPlatformAccountUuid === this.clientPortalProvider().providerAccountId);
        this.hasMatchedWithProvider.set(!!crm);
        this.clientReferralMemberId = crm?.id;
      })
    ).subscribe(() => {
      this.isClientPortalProviderLoading.set(false);
      this.initializeAuthenticatedCalendar(this.providerShareableProfile);
    });
  }

  onRequestSessionButtonClicked(
    requestedSessionPreferences?: ISessionRequestPreferences,
  ): void {
    const isNativeApp = NativeAppContextHelpers.isHostedInNativeApp();

    if (!this.isScheduled && isNativeApp) {
      this.onNativeAppBookedASession(requestedSessionPreferences);
      return;
    }
    this.requestASessionService.onRequestSessionButtonClicked({
      profile: this.providerShareableProfile,
      providerPrimaryAddress: this.providerPrimaryAddress,
      queryParams: this.requestSessionQueryParams,
      requestedSessionPreferences: {
        startTime: requestedSessionPreferences?.startTime,
        endTime: requestedSessionPreferences?.endTime,
        sessionType: requestedSessionPreferences?.sessionType,
      },
      shareCode: this.paramsShareCode,
      isInternal: this.isInternal
    });
  }

  onMobileDrawerButtonClicked(): void {
    // if calendar is on the page, scroll to it and focus on the first time slot
    // otherwise, open quick checkout modal
    const scrollTarget: HTMLElement = document.getElementById(
      'public-provider-calendar',
    );
    const focusTarget: HTMLElement = document.querySelector('.carousel-time');

    if (scrollTarget && focusTarget) {
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      focusTarget.focus({ preventScroll: true });
    } else {
      this.onRequestSessionButtonClicked();
    }
  }

  areActionsScrolledIntoView(): void {
    if (this.actionsCard) {
      const rect = this.actionsCard.nativeElement.getBoundingClientRect();
      // If the actions component is bigger than the whole screen on mobile, use the bigger value
      const totalHeight = Math.max(
        this.actionsCard.nativeElement.clientHeight,
        this.window.visualViewport.height,
      );
      const isTallerThanViewportHeight =
        this.actionsCard.nativeElement.clientHeight >
        this.window.visualViewport.height;

      const isTopShown = rect.top >= -totalHeight + MOBILE_BOTTOM_DRAWER_HEIGHT;
      const isBottomShown =
        rect.bottom <= window.innerHeight + MOBILE_BOTTOM_DRAWER_HEIGHT;
      this.isCalendarScrolledIntoView = isTallerThanViewportHeight
        ? isTopShown
        : isTopShown && isBottomShown;
    }
  }

  private sendPageViewSegmentEvent(): void {
    this.showProviderCalendar$.pipe(
      tap((showProviderCalendar) => {
        // Get UTM parameters
        const urlParams = this.route.snapshot.queryParams;
        const utmParams = Object.entries(urlParams).filter(([key]) => key.startsWith('utm'));

        // Get params from directory, if they exist
        const directoryFilters = {
          state: this.queryParamMap.get('state'),
          insurance: this.queryParamMap.get('paymentTag'),
          modalities: this.queryParamMap.get('modalities'),
          specialties: this.queryParamMap.get('specialties'),
          genders: this.queryParamMap.get('genders'),
        };

        // Cut off any query params
        const referringDomain = this.document.referrer.slice(
          0,
          this.document.referrer.indexOf('?'),
        );
        const referringUrl: string =
          this.queryParamMap.get('referringUrl') ?? '';

        const baseEventData = {
          accepting: this.providerShareableProfile.accepting_clients,
          directoryFilters,
          internalReferringPage: referringUrl,
          isCalendarVisible: showProviderCalendar,
          personaProviderId: this.providerShareableProfile.id,
          referringDomain,
          utm: utmParams,
        } as IPslPageLoadEventData;

        // if we SHOW the calendar, include calendar initialization data.
        const pageLoadData$: Observable<IPslPageLoadEventData> = showProviderCalendar
          ? this.calendarSegmentData$.pipe(
            filter((calendarData) => !!calendarData),
            take(1),
            map((calendarData: IProviderCalendarInitSegmentData) => ({
              ...baseEventData,
              ...calendarData
            })))
          : of(baseEventData);

        pageLoadData$.pipe(
          switchMap(
            (eventData) => from(
              this.segmentService.track<SondermindEvent.ViewedAProviderShareableLinkPage>(
                SondermindEvent.ViewedAProviderShareableLinkPage,
                eventData
              )
            )
          )
        ).subscribe();
      }),
      take(1)
    ).subscribe();
  }

  private updateMetaInfo(
    providerName: string,
    certifications: Array<{ name: string; abbrev: string; }>,
    bio: string,
  ): void {
    const certs: string = certifications?.map((obj) => obj.name).join(', ');
    const title = `${providerName}, ${certs} | SonderMind`;
    // extract the first sentence of the bio;
    const sentence = /[^.!?]*[.!?]/.exec(bio);
    const firstSentenceofBio = sentence ? sentence[0] : bio;
    const first160CharsofBio = firstSentenceofBio
      ? firstSentenceofBio.substring(0, 159)
      : ''; // bio can be null
    const description = `${firstSentenceofBio ?? ''}`;

    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({
      property: 'og:url',
      content: this.window.location.href,
    });
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({
      property: 'og:description',
      content: first160CharsofBio,
    });
    this.metaService.updateTag({
      property: 'twitter:url',
      content: this.window.location.href,
    });
    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({
      name: 'twitter:description',
      content: first160CharsofBio,
    });
  }

  private updateCanonicalUrl(): void {
    if (!this.paramsShareCode || (!this.paramsName && !this.providerShareableProfile.display_name)) {
      // Don't set canonical if we don't have the necessary data.
      return;
    }
    let canonicalUrl = this.configurationService.env.providerSharableLinkBase ?? 'https://www.sondermind.com/providers';
    let profileName = this.paramsName;
    if (!this.paramsName) {
      profileName = this.providerShareableProfile.display_name.toLocaleLowerCase().split(' ').join('-');
    }
    canonicalUrl = `${canonicalUrl}/${this.paramsShareCode}/${profileName}`;

    let link = this.document.querySelector("link[rel='canonical']");
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
  }

  /**
   * Sets optimal state for browse-therapists-card based on acceptance criteria:
   * 1. If coming from directory → use query parameter state
   * 2. If NOT coming from directory → use provider's licensed state
   */
  private setOptimalStateForBrowseCard(): void {
    const queryState = this.queryParamMap.get('state');

    const stateAbbrev = queryState
      ? this.convertToStateAbbrev(queryState)
      : this.getOptimalStateAbbrev();

    this.currentStateAbbrev$.next(stateAbbrev);
  }

  private getOptimalStateAbbrev(): string {
    const certificationStates = new Set(
      this.providerShareableProfile.certifications?.map((cert) => cert.issue_state) || []
    );

    const matchedState = this.providerShareableProfile.location_accesses_states?.find(
      (state) => certificationStates.has(state.abbrev)
    );

    if (matchedState) {
      return matchedState.abbrev;
    }

    return (
      this.providerShareableProfile.location_accesses_states?.[0]?.abbrev ||
      this.providerShareableProfile.certifications?.[0]?.issue_state ||
      ''
    );
  }

  onAiConnectModuleViewed(): void {
    this.segmentService.track<SondermindEvent.AiConnectModuleViewed>(
      SondermindEvent.AiConnectModuleViewed,
      {
        project: AiConnectProjectType.PSL,
        component: AiConnectComponent.FIND_MY_FIT,
        variant: this.directoryCompanionVariant()
      }
    ).catch(() => { });
  }

  onAiConnectStartButtonClicked(selectedPills: string[]): void {
    this.segmentService.track<SondermindEvent.AiConnectModuleStartButtonClicked>(
      SondermindEvent.AiConnectModuleStartButtonClicked,
      { project: AiConnectProjectType.PSL, selectedPills }
    ).catch(() => { });
  }

  /**
   * Converts state name or abbreviation to standard abbreviation format
   */
  findMyFitClicked(chips: IFindMyFitChip[]): void {
    const stateAbbrev = this.currentStateAbbrev$.getValue();
    const relevantChips = chips.filter((chip) => chip.formKey && chip.values.length);

    const genders = relevantChips
      .filter((chip) => chip.formKey === 'genders')
      .flatMap((chip) => chip.values.map((v) => v.title));

    const specialties = relevantChips
      .filter((chip) => chip.formKey === 'specialties')
      .flatMap((chip) => chip.values.map((v) => v.tag));

    const modalities = relevantChips
      .filter((chip) => chip.formKey === 'serviceType')
      .flatMap((chip) => chip.values.map((v) => v.tag));

    const ages = relevantChips
      .filter((chip) => chip.formKey === 'ages')
      .flatMap((chip) => chip.values.map((v) => v.title));

    const insuranceChip = relevantChips.find((chip) => chip.formKey === 'insurance');
    const insuranceTitle = insuranceChip?.values[0]?.title;

    const url = this.directoryLinkService.buildProviderDirectoryLink(
      stateAbbrev,
      undefined,
      insuranceTitle,
      genders,
      modalities,
      specialties,
      ages
    );

    this.window.open(`${url}?launchCompanion=true`, '_blank');
  }

  private convertToStateAbbrev(state: string): string {
    const stateUpper = state.trim().toUpperCase();

    const matchedEntry = Object.entries(USStateAbbrevMap).find(
      ([, fullName]) => fullName.toUpperCase() === stateUpper
    );

    return matchedEntry ? matchedEntry[0] : stateUpper;
  }
}
